/**
 * Meta Pixel Sprint 2026-08 — the ONLY place that fires the three
 * lifecycle standard events (CompleteRegistration / StartTrial /
 * Subscribe).
 *
 * Contract:
 *   * Consumes ``user`` from ``AuthContext`` (may be null).
 *   * Fires each event AT MOST once per browser, per account —
 *     the backend ``POST /api/analytics/meta-event-mark`` endpoint
 *     is the source of truth. If it returns ``fired=false`` we
 *     stay silent.
 *   * Never sends the ULID, email, or any other user identifier to
 *     Meta. Only the standard event name goes on the wire.
 *   * A restore / reactivate / cache-clear does NOT re-fire — the
 *     backend markers survive both.
 *
 * The three prerequisites are read directly from ``user``:
 *
 *   * ``email_verified === true``                         → registration
 *   * ``trial_first_started_at`` not null                 → trial
 *   * ``subscription_first_paid_at`` not null             → subscribe
 *
 * The hook is intentionally passive: it only fires WHILE it can. If
 * consent isn't granted yet, the wrapper is a no-op. When consent
 * is granted later, the hook still owns the state → it will fire
 * at the next relevant render.
 */
import { useEffect, useRef, useState } from "react";
import { track } from "../lib/metaPixel";
import {
  getMarketingConsent,
  subscribe as subscribeConsent,
} from "../consent/consent";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function _postMark(eventType, token) {
  // Best-effort — a failure MUST NOT surface to the user or throw.
  try {
    const res = await fetch(`${API_URL}/api/analytics/meta-event-mark`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({ event_type: eventType }),
    });
    if (!res.ok) return { fired: false };
    return await res.json();
  } catch {
    return { fired: false };
  }
}

export default function useMetaLifecycleEvents(user, accessToken) {
  // In-memory per-render dedupe — belt & suspenders on top of the
  // backend marker (so we don't spam POST /meta-event-mark on every
  // render when the state settled).
  const ranRef = useRef({
    registration: false, trial: false, subscribe: false,
  });

  // P0.2 review — this hook was previously reading marketing
  // consent inside the effect body only, so it never re-ran when
  // the user granted consent AFTER the user object was loaded.
  // Turning it into subscribed React state fixes that: the effect
  // below lists ``consent`` in its dependency array and re-runs the
  // exact moment consent flips.
  const [consent, setConsent] = useState(getMarketingConsent());
  useEffect(() => {
    const off = subscribeConsent((c) => {
      setConsent(Boolean(c.decided && c.categories.marketing));
    });
    return () => off && off();
  }, []);

  useEffect(() => {
    if (!user || !accessToken) return;
    if (!consent) return;

    async function _maybeFire(eventType, standardEvent, prereqOk) {
      if (!prereqOk) return;
      if (ranRef.current[eventType]) return;
      ranRef.current[eventType] = true;
      const res = await _postMark(eventType, accessToken);
      if (res && res.fired === true) {
        track(standardEvent);
      }
    }

    // registration — only after email verification.
    _maybeFire(
      "registration", "CompleteRegistration",
      Boolean(user.email_verified),
    );
    // trial — only after backend's set-once timestamp exists.
    _maybeFire(
      "trial", "StartTrial",
      Boolean(user.trial_first_started_at),
    );
    // subscribe — only after a REAL paid activation. Requires BOTH
    // (a) the set-once ``subscription_first_paid_at`` server marker
    // AND (b) an active Stripe status right now. Restore of an
    // expired sub keeps the marker but flips status ≠ active, so
    // this correctly refuses to re-fire.
    _maybeFire(
      "subscribe", "Subscribe",
      Boolean(
        user.subscription_first_paid_at
        && user.subscription_status === "active",
      ),
    );
  }, [
    user?.ulid,
    user?.email_verified,
    user?.trial_first_started_at,
    user?.subscription_first_paid_at,
    user?.subscription_status,
    accessToken,
    consent,
  ]);
}
